import { useEffect, useState } from "react";
import { BarChart3, Loader2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { formatPrice } from "@/components/site/EventCard";

const SALES_CHART_GRANULARITIES = [
  { key: "day", label: "Per dag" },
  { key: "week", label: "Per week" },
  { key: "year", label: "Per jaar" },
];

const SALES_CHART_METRICS = [
  { key: "revenue_cents", label: "Omzet", format: (v) => formatPrice(v || 0) },
  { key: "tickets_qty", label: "Tickets verkocht", format: (v) => String(v || 0) },
  { key: "orders_count", label: "Bestellingen", format: (v) => String(v || 0) },
];

// Verkopen-over-tijd balkgrafiek -- gedeeld tussen de per-event Rapportage-tab
// (AdminReportDetail, endpoint=/admin/reports/{eventId}/sales-timeseries) en
// het Dashboard (AdminDashboard, endpoint=/admin/reports/sales-timeseries,
// geaggregeerd en rol-gescoped net als de rest van het dashboard). Beide
// endpoints geven exact dezelfde {granularity, buckets} vorm terug, dus is
// er maar één component nodig -- alleen de URL en de titel verschillen.
export default function SalesChart({ endpoint, authAxios, title = "Verkopen over tijd", testid = "sales-chart" }) {
  const [granularity, setGranularity] = useState("day");
  const [metric, setMetric] = useState("revenue_cents");
  const [buckets, setBuckets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authAxios
      .get(endpoint, { params: { granularity } })
      .then(({ data }) => {
        if (!cancelled) setBuckets(data.buckets || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpoint, granularity, authAxios]);

  const activeMetric = SALES_CHART_METRICS.find((m) => m.key === metric);

  return (
    <div className="bg-svj-card border border-svj-border rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-slate-400">
          <BarChart3 className="h-4 w-4 text-svj-cyan" />
          <span className="text-[11px] uppercase tracking-widest font-semibold">{title}</span>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex gap-1 bg-svj-bg border border-svj-border rounded-full p-1">
            {SALES_CHART_GRANULARITIES.map((g) => (
              <button
                key={g.key}
                type="button"
                data-testid={`${testid}-granularity-${g.key}`}
                onClick={() => setGranularity(g.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                  granularity === g.key ? "bg-svj-cyan text-svj-bg" : "text-slate-400 hover:text-white"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 bg-svj-bg border border-svj-border rounded-full p-1">
            {SALES_CHART_METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                data-testid={`${testid}-metric-${m.key}`}
                onClick={() => setMetric(m.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                  metric === m.key ? "bg-svj-orange text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 h-72">
        {loading ? (
          <div className="h-full flex items-center justify-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin text-svj-cyan" />
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-sm text-red-400">{error}</div>
        ) : !buckets || buckets.length === 0 ? (
          <div className="h-full flex items-center justify-center text-sm text-slate-400">
            Nog geen betaalde bestellingen voor deze periode.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={buckets} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3050" vertical={false} />
              <XAxis
                dataKey="period"
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                axisLine={{ stroke: "#1e3050" }}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={16}
              />
              <YAxis
                tick={{ fill: "#94a3b8", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (metric === "revenue_cents" ? formatPrice(v) : v)}
                width={metric === "revenue_cents" ? 72 : 36}
              />
              <Tooltip
                cursor={{ fill: "#1e3050", opacity: 0.4 }}
                contentStyle={{ background: "#162235", border: "1px solid #1e3050", borderRadius: 8, color: "#fff" }}
                labelStyle={{ color: "#94a3b8" }}
                formatter={(value) => [activeMetric.format(value), activeMetric.label]}
              />
              <Bar dataKey={metric} fill="#06b6d4" radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
